/* ============================================================
   GG WISHLIST — self-hosted, no app, no monthly fee.

   Storage: localStorage, one array of product handles under GG_KEY.
   Handles, not variant or product IDs, because a handle is all that
   /products/<handle>.js needs and it stays readable if anyone inspects it.

   DELIBERATELY per-device. Cross-device would mean storing the list against a
   customer server-side, and theme JavaScript cannot write customer metafields —
   that needs an authenticated backend. Documented in CLAUDE.md so nobody
   rediscovers it as a bug.

   Product data on the wishlist page comes from /products/<handle>.js, a built-in
   Shopify endpoint. No extra template, no Liquid loop over a stored list.
   ============================================================ */
(function () {
  'use strict';

  var GG_KEY = 'gg_wishlist';

  /* ---------- store ---------- */

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(GG_KEY) || '[]');
      // Tolerate the older {handle:…} object format and anything hand-edited.
      return raw
        .map(function (x) { return typeof x === 'string' ? x : (x && x.handle); })
        .filter(Boolean);
    } catch (e) {
      return [];   // corrupt or disabled storage: behave as empty, never throw
    }
  }

  function write(list) {
    try {
      localStorage.setItem(GG_KEY, JSON.stringify(list));
    } catch (e) {
      /* Safari private mode throws on setItem. Nothing to do but carry on —
         the UI still reflects this session. */
    }
    document.dispatchEvent(new CustomEvent('gg:wishlist:change', { detail: list }));
  }

  function has(handle) { return read().indexOf(handle) > -1; }

  function toggle(handle) {
    var list = read();
    var i = list.indexOf(handle);
    if (i > -1) { list.splice(i, 1); } else { list.push(handle); }
    write(list);
    return i === -1;         // true when it was just added
  }

  /* ---------- hearts ---------- */

  function paintHeart(btn) {
    var on = has(btn.dataset.productHandle);
    btn.classList.toggle('gg-is-saved', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function paintAllHearts() {
    document.querySelectorAll('.gg-wishlist-heart').forEach(paintHeart);
  }

  // Delegated: product grids are re-rendered by the filter AJAX, so binding to
  // each heart would lose the handler on every filter change.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.gg-wishlist-heart');
    if (!btn) return;
    // Crave's card has a full-card invisible link overlay; without this the
    // click navigates to the product instead of toggling.
    e.preventDefault();
    e.stopPropagation();
    toggle(btn.dataset.productHandle);
    paintHeart(btn);
  });

  /* No header count. Removed at the client's request — a number beside the
     heart added noise without telling the shopper anything they could act on,
     and the wishlist page is one tap away. */

  /* ---------- wishlist page ---------- */

  function money(cents) {
    // Shopify's money format is per-shop; the root element carries it so the
    // page does not hardcode a currency.
    var fmt = document.documentElement.getAttribute('data-gg-money') || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount\s*\}\}/, amount)
              .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
              .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  }

  function card(p) {
    var img = p.featured_image
      ? '<img src="' + p.featured_image + '&width=600" alt="" loading="lazy" width="600" height="800">'
      : '';
    // First AVAILABLE variant, not variants[0] — these products carry a Size
    // option, and the first one is often the sold-out one.
    var v = null;
    for (var k = 0; k < p.variants.length; k++) {
      if (p.variants[k].available) { v = p.variants[k]; break; }
    }
    var cta = v
      ? '<button type="button" class="gg-wl__add" data-gg-add="' + v.id + '">Add to cart</button>'
      : '<button type="button" class="gg-wl__add" disabled>Sold</button>';
    // Heart sits on the TITLE line, matching the product cards (gg-luxury §13),
    // rather than floating over the photograph. That needs it as a sibling of the
    // title in a flex row, not absolutely positioned over the image.
    return '' +
      '<li class="gg-wl__item">' +
        '<a class="gg-wl__link" href="/products/' + p.handle + '">' +
          '<span class="gg-wl__media">' + img + '</span>' +
        '</a>' +
        '<div class="gg-wl__row">' +
          '<a class="gg-wl__title" href="/products/' + p.handle + '">' + p.title + '</a>' +
          '<button type="button" class="gg-wishlist-heart gg-is-saved" ' +
            'data-product-handle="' + p.handle + '" aria-pressed="true" ' +
            'aria-label="Remove from wishlist">' +
            '<svg viewBox="3 4 18 17.5" width="20" height="20" aria-hidden="true" focusable="false">' +
            '<path d="M12 20.5S3.5 14.9 3.5 9.4a4.6 4.6 0 0 1 8.5-2.5 4.6 4.6 0 0 1 8.5 2.5c0 5.5-8.5 11.1-8.5 11.1Z" ' +
            'fill="none" stroke="currentColor" stroke-width="1" stroke-linejoin="round"/></svg>' +
          '</button>' +
        '</div>' +
        '<span class="gg-wl__price">' + money(p.price) + '</span>' +
        cta +
      '</li>';
  }

  /* ---------- add to cart ---------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-gg-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    // `sections: 'header'` asks Shopify to return the re-rendered header with the
    // add, so the cart bubble updates without a page load. Crave omits the bubble
    // entirely when the cart is empty, so incrementing its text is not enough —
    // the markup has to come back from the server.
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(btn.dataset.ggAdd), quantity: 1, sections: 'header' })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (data) {
        btn.textContent = 'Added';
        if (data.sections && data.sections.header) {
          var doc = new DOMParser().parseFromString(data.sections.header, 'text/html');
          var fresh = doc.querySelector('#cart-icon-bubble');
          var live = document.querySelector('#cart-icon-bubble');
          if (fresh && live) { live.innerHTML = fresh.innerHTML; }
        }
        setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 1800);
      })
      .catch(function () {
        btn.textContent = 'Unavailable';
        setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 1800);
      });
  });

  function renderPage() {
    var root = document.querySelector('[data-gg-wishlist-page]');
    if (!root) return;
    var grid = root.querySelector('[data-gg-wishlist-grid]');
    var empty = root.querySelector('[data-gg-wishlist-empty]');
    var list = read();

    if (!list.length) {
      grid.innerHTML = '';
      grid.hidden = true;
      empty.hidden = false;
      return;
    }
    empty.hidden = true;
    grid.hidden = false;

    Promise.all(list.map(function (h) {
      return fetch('/products/' + h + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        // A deleted or unpublished product 404s. Drop it rather than break the
        // whole page, and prune it from storage so it stops being requested.
        .catch(function () { return null; });
    })).then(function (products) {
      var alive = products.filter(Boolean);
      if (alive.length !== list.length) {
        write(alive.map(function (p) { return p.handle; }));
      }
      grid.innerHTML = alive.map(card).join('');
      if (!alive.length) { grid.hidden = true; empty.hidden = false; }
    });
  }

  /* ---------- wiring ---------- */

  function init() {
    paintAllHearts();
    renderPage();
  }

  document.addEventListener('gg:wishlist:change', function () {
    // On the wishlist page, un-hearting should remove the card immediately.
    if (document.querySelector('[data-gg-wishlist-page]')) { renderPage(); }
  });

  // Re-paint after Shopify swaps a section (filters, cart drawer, editor).
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState !== 'loading') { init(); }

  // The collection filters replace the grid without firing a section event, so
  // watch for new hearts appearing.
  if (window.MutationObserver) {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () { pending = false; paintAllHearts(); });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.ggWishlist = { read: read, has: has, toggle: toggle };
})();
